import { Chip, Tooltip, Typography, makeStyles } from '@material-ui/core';
import AssessmentIcon from '@material-ui/icons/Assessment';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import WarningIcon from '@material-ui/icons/Warning';
import type {
  TraceReliabilityIndicator,
  TraceReliabilityState,
} from '../../api/types';

export interface ReliabilityBadgeProps {
  indicator: TraceReliabilityIndicator;
  size?: 'small' | 'medium';
}

const useStyles = makeStyles(theme => ({
  details: {
    padding: theme.spacing(0.5),
    maxWidth: 280,
  },
  detailLine: {
    display: 'block',
    marginTop: theme.spacing(0.25),
  },
}));

export function buildReliabilityAriaLabel(
  indicator: TraceReliabilityIndicator,
): string {
  return [
    `Estado: ${indicator.label}`,
    `Fonte: ${indicator.source}`,
    `Período: ${indicator.period}`,
    `Cobertura: ${indicator.coverage}`,
    `Atualização: ${indicator.updatedAt ?? 'não disponível'}`,
    indicator.currency ? `Moeda: ${indicator.currency}` : null,
    `Explicação: ${indicator.explanation}`,
  ]
    .filter(Boolean)
    .join('. ');
}

export const ReliabilityBadge = ({
  indicator,
  size = 'small',
}: ReliabilityBadgeProps) => {
  const icon = getReliabilityIcon(indicator.state);

  return (
    <Tooltip title={<ReliabilityTooltip indicator={indicator} />}>
      <Chip
        size={size}
        variant="outlined"
        icon={icon}
        label={indicator.label}
        aria-label={buildReliabilityAriaLabel(indicator)}
      />
    </Tooltip>
  );
};

function ReliabilityTooltip({
  indicator,
}: {
  indicator: TraceReliabilityIndicator;
}) {
  const classes = useStyles();

  return (
    <span className={classes.details}>
      <Typography variant="caption" component="span">
        {indicator.explanation}
      </Typography>
      <Typography
        className={classes.detailLine}
        component="span"
        variant="caption"
      >
        Fonte: {indicator.source}
      </Typography>
      <Typography
        className={classes.detailLine}
        component="span"
        variant="caption"
      >
        Período: {indicator.period}
      </Typography>
      <Typography
        className={classes.detailLine}
        component="span"
        variant="caption"
      >
        Cobertura: {indicator.coverage}
      </Typography>
      {indicator.currency && (
        <Typography
          className={classes.detailLine}
          component="span"
          variant="caption"
        >
          Moeda: {indicator.currency}
        </Typography>
      )}
    </span>
  );
}

function getReliabilityIcon(state: TraceReliabilityState): React.ReactElement {
  switch (state) {
    case 'confirmed':
      return <CheckCircleIcon />;
    case 'calculated':
      return <AssessmentIcon />;
    case 'estimated':
      return <ScheduleIcon />;
    case 'partial':
      return <WarningIcon />;
    case 'unavailable':
      return <ErrorOutlineIcon />;
    case 'not-applicable':
    default:
      return <HelpOutlineIcon />;
  }
}
